/** The deck card itself: the statements the "Norican — last 90 days" card makes.
 *
 *  This is the panel the whole board exists for. The quarterly deck's "Norican —
 *  last 90 days" card was assembled by hand from a scraped TAP dashboard, which
 *  means the numbers could not be recomputed, could not be trended, and could
 *  not be defended when someone asked where they came from. Here each one is a
 *  query with its definition attached, phrased the way the slide phrases it, and
 *  copyable in one click.
 *
 *  Two honesty rules do the heavy lifting:
 *
 *  - A statement with no data behind it says so and shows an em dash. "We
 *    blocked 0 dangerous emails" and "we have not collected anything yet" are
 *    very different claims to put in front of a board.
 *  - Tone comes from the backend, and it is never colour-alone: every statement
 *    carries its own detail line explaining what the number counts.
 */

import { Copy, Icon } from "@norican/icons";
import { Badge, Button, Card, Statistic, Typography } from "@norican/react";
import { useState } from "react";

import { VerdictFooter } from "@/components/board";
import type { QuarterSnapshot as QuarterSnapshotPayload } from "@/lib/api";
import { logger } from "@/lib/logger";

import { statementTone } from "./status";

interface QuarterSnapshotProps {
  snapshot: QuarterSnapshotPayload;
  /** The pinned definition, restated here because this panel gets pasted. */
  definition: string;
}

export function QuarterSnapshot({ snapshot, definition }: QuarterSnapshotProps) {
  const [copied, setCopied] = useState(false);
  const missing = snapshot.statements.filter((statement) => !statement.available).length;

  const asText = () =>
    [
      `Norican, last ${snapshot.window_days} days (${snapshot.start_day} to ${snapshot.end_day})`,
      "",
      ...snapshot.statements.map((statement) => `- ${statement.statement}`),
      "",
      definition,
    ].join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.warn("[security] could not copy the quarter snapshot", error);
    }
  };

  return (
    <section id="security-quarter" className="scroll-mt-6">
      <Card variant="outline">
        <Card.Header>
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <Typography variant="heading-4" as="h3">
              The deck card
            </Typography>
            <div className="flex flex-wrap items-center gap-3">
              <Typography variant="body-sm" tone="muted">
                {snapshot.start_day} to {snapshot.end_day}
              </Typography>
              <Button
                size="sm"
                variant="ghost"
                startIcon={<Icon icon={Copy} size="sm" />}
                onClick={copy}
              >
                {copied ? "Copied" : "Copy for the deck"}
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Typography variant="body-sm" tone="muted">
              The lines the quarterly leadership deck carries on its &ldquo;Norican — last 90
              days&rdquo; card, with the numbers NOVA measured. This replaces the browser scrape:
              every figure below is recomputable from stored daily counts, and every one states what
              it counts.
            </Typography>

            <ol className="flex flex-col gap-3">
              {snapshot.statements.map((statement) => (
                <li key={statement.id}>
                  <div className="border-border bg-surface flex flex-col gap-2 rounded-md border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <Typography variant="body" as="p" className="max-w-2xl">
                        {statement.statement}
                      </Typography>
                      <Statistic
                        label={statement.id.replace("_", " ")}
                        value={statement.value}
                        tone={statementTone(statement)}
                        size="sm"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {!statement.available && (
                        <Badge tone="neutral" variant="subtle" size="sm">
                          No data yet
                        </Badge>
                      )}
                      <Typography variant="body-sm" tone="muted">
                        {statement.detail}
                      </Typography>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Card.Body>
        <Card.Footer>
          <VerdictFooter
            verdict={
              missing === 0
                ? {
                    label: "Ready",
                    tone: "success",
                    text: "Every line on the card is measured and recomputable. Paste it as it stands.",
                  }
                : {
                    label: "Partial",
                    tone: "warning",
                    text: `${missing} of ${snapshot.statements.length} lines have no data behind them yet — fix collection before this card goes into a deck.`,
                  }
            }
          />
        </Card.Footer>
      </Card>
    </section>
  );
}
